import React, { useState, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { X, Upload, AlertCircle } from 'lucide-react';

const PAYMENT_TYPES = [
  { value: 'analysis_fee', label: '₹49 — Credit Report Analysis Fee', amount: 49, fixed: true },
  { value: 'consultation', label: '₹1,999 — Legal Consultation Fee', amount: 1999, fixed: true },
  { value: 'enrollment', label: 'Enrollment Payment (Full 1st Month Fee)', amount: null },
  { value: 'partial', label: 'Partial Payment (with next payment date)', amount: null },
];

export function PaymentModal({ leadId, leadName, proposalFee, onClose, onSave }) {
  const [form, setForm] = useState({ type: 'analysis_fee', amount: '49', mode: 'UPI', notes: '', utr: '', nextPaymentDate: '' });
  const [screenshot, setScreenshot] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const isRazorpay = form.mode === 'Razorpay';
  const selectedType = PAYMENT_TYPES.find(t => t.value === form.type);

  const handleTypeChange = (type) => {
    const t = PAYMENT_TYPES.find(p => p.value === type);
    set('type', type);
    if (t?.fixed) set('amount', String(t.amount));
    else if (type === 'enrollment' && proposalFee) set('amount', String(proposalFee));
    else set('amount', '');
  };

  const save = async () => {
    if (!form.amount) return toast.error('Amount is required.');
    if (form.type === 'partial' && !form.nextPaymentDate) return toast.error('Next payment date is mandatory for partial payments.');
    if (!isRazorpay && !screenshot) return toast.error('Payment screenshot is mandatory for manual payments.');

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('leadId', leadId);
      fd.append('type', form.type === 'partial' ? 'enrollment' : form.type);
      fd.append('amount', form.amount);
      fd.append('mode', form.mode);
      fd.append('notes', form.notes);
      fd.append('utr', form.utr);
      if (form.nextPaymentDate) fd.append('nextPaymentDate', form.nextPaymentDate);
      if (screenshot) fd.append('screenshot', screenshot);

      await api.post('/payments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });

      toast.success(
        isRazorpay
          ? 'Payment recorded and auto-verified.'
          : 'Payment recorded. Pending TL/Admin verification before it reflects in targets.',
        { duration: 5000 }
      );
      onSave();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to record payment.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <div className="modal-title">Record Payment — {leadName}</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">

          {/* Manual payment warning */}
          {!isRazorpay && (
            <div style={{ padding: '10px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <AlertCircle size={16} style={{ color: 'var(--warning)', flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 12, color: '#92400e' }}>
                Manual payments require <strong>screenshot upload</strong> and <strong>TL/Admin verification</strong> before reflecting in targets.
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Payment Type *</label>
            <select className="form-control" value={form.type} onChange={e => handleTypeChange(e.target.value)}>
              {PAYMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>

          <div className="grid grid-2">
            <div className="form-group">
              <label className="form-label">Amount (₹) *</label>
              <input
                className="form-control"
                type="number"
                value={form.amount}
                onChange={e => set('amount', e.target.value)}
                readOnly={selectedType?.fixed}
                style={{ background: selectedType?.fixed ? 'var(--surface)' : 'white' }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Payment Mode *</label>
              <select className="form-control" value={form.mode} onChange={e => set('mode', e.target.value)}>
                {['UPI','Razorpay','NEFT/RTGS','Bank Transfer','Cash','Cheque'].map(m => <option key={m}>{m}</option>)}
              </select>
            </div>
          </div>

          {form.type === 'partial' && (
            <div className="form-group">
              <label className="form-label">Next Payment Date * <span style={{ color: 'var(--danger)' }}>(Mandatory for partial)</span></label>
              <input className="form-control" type="date" value={form.nextPaymentDate} onChange={e => set('nextPaymentDate', e.target.value)} min={new Date().toISOString().split('T')[0]} />
            </div>
          )}

          <div className="form-group">
            <label className="form-label">UTR / Transaction ID</label>
            <input className="form-control" value={form.utr} onChange={e => set('utr', e.target.value)} placeholder="Optional but recommended" />
          </div>

          {/* Screenshot upload — mandatory for manual */}
          {!isRazorpay && (
            <div className="form-group">
              <label className="form-label">
                Payment Screenshot * <span style={{ color: 'var(--danger)' }}>(Mandatory)</span>
              </label>
              <div
                style={{ border: `2px dashed ${screenshot ? 'var(--success)' : 'var(--danger)'}`, borderRadius: 8, padding: '14px', textAlign: 'center', cursor: 'pointer', background: screenshot ? '#f0fdf4' : '#fef2f2' }}
                onClick={() => fileRef.current.click()}
              >
                <input ref={fileRef} type="file" accept="image/*,.pdf" style={{ display: 'none' }} onChange={e => setScreenshot(e.target.files[0])} />
                {screenshot ? (
                  <div style={{ color: 'var(--success)', fontWeight: 600 }}>✓ {screenshot.name}</div>
                ) : (
                  <>
                    <Upload size={20} style={{ color: 'var(--danger)', marginBottom: 6 }} />
                    <div style={{ fontSize: 13, color: 'var(--danger)', fontWeight: 600 }}>Upload payment screenshot (required)</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>JPG, PNG, PDF accepted</div>
                  </>
                )}
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-control" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Optional notes..." />
          </div>

          {!isRazorpay && (
            <div style={{ padding: '8px 12px', background: 'var(--surface)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)' }}>
              ℹ️ This payment will show as <strong>Pending Verification</strong> until your TL verifies it. Only then it will count toward your target.
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-success" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" /> : '✓ Record Payment'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function PromiseModal({ leadId, onClose, onSave }) {
  const [form, setForm] = useState({ promiseDate: '', promiseAmount: '', remarks: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.promiseDate) return toast.error('Promise date required');
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/promise`, form);
      onSave();
    } catch (e) {
      toast.error('Failed to save promise');
    } finally {
      setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <div className="modal-title">Set Payment Promise</div>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Promise Date</label>
            <input className="form-control" type="date" value={form.promiseDate} onChange={e => set('promiseDate', e.target.value)} min={new Date().toISOString().split('T')[0]} />
          </div>
          <div className="form-group">
            <label className="form-label">Expected Amount (₹)</label>
            <input className="form-control" type="number" value={form.promiseAmount} onChange={e => set('promiseAmount', e.target.value)} placeholder="10000" />
          </div>
          <div className="form-group">
            <label className="form-label">Remarks</label>
            <textarea className="form-control" value={form.remarks} onChange={e => set('remarks', e.target.value)} placeholder="Client confirmed payment on salary date..." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? <span className="spinner" /> : '🤝 Save Promise'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default PaymentModal;
